import { WorkCategory } from '../services/messageQueue.service';

export interface ReviewableWorkItem {
	userId: number | null;
	projectId: number | null;
	clientId: number | null;
	precinctId: number | null;
	blockId: number | null;
	blockLevelFrom: number | null;
	blockLevelTo: number | null;
	blockUnitFrom: number | null;
	blockUnitTo: number | null;
	blockUnitNumberPattern: 'continuous' | 'odd' | 'even' | null;
	typeId: number | null;
	subTypeId: number | null;
	subTypeValueId: number | null;
	level: number | null;
	unit: number | null;
	quantity: number | null;
	quantityUnit: string;
	performedAt: string;
	workCategory: WorkCategory;
	isClientBillable: boolean;
	isWorkerCreditable: boolean;
	reportedLocationText: string;
	helperUserIds: number[];
}

export interface WorkItemReviewOptions {
	/** False only when the selected project has no precincts configured. */
	requireStructuredLocation?: boolean;
}

/**
 * Form fields a review issue can be attributed to, so the review dialog can
 * put a red border on the specific control(s) that need attention instead
 * of only showing the "Please correct: ..." summary line.
 */
export type WorkItemIssueField =
	| 'userId'
	| 'projectId'
	| 'clientId'
	| 'typeId'
	| 'subTypeId'
	| 'subTypeValueId'
	| 'quantity'
	| 'quantityUnit'
	| 'performedAt'
	| 'precinctId'
	| 'blockId'
	| 'level'
	| 'unit'
	| 'reportedLocationText'
	| 'workCategory';

export interface WorkItemReviewIssue {
	/** Which field(s) this issue applies to; empty when no single control owns it. */
	fields: WorkItemIssueField[];
	/** Full text for the bottom "Please correct" summary line. */
	message: string;
	/**
	 * Short text for that field's own helper text, when the full `message`
	 * would be too long to repeat under every one of several fields it
	 * applies to. Falls back to `message` when not set.
	 */
	fieldHint?: string;
}

export const CATEGORY_DEFAULTS: Record<WorkCategory, { isClientBillable: boolean; isWorkerCreditable: boolean }> = {
	normal: { isClientBillable: true, isWorkerCreditable: true },
	free_redo: { isClientBillable: false, isWorkerCreditable: true },
	rectify: { isClientBillable: false, isWorkerCreditable: true },
	fabrication: { isClientBillable: false, isWorkerCreditable: true },
};

const FIELD_LABELS: Array<[keyof ReviewableWorkItem, string]> = [
	['userId', 'Worker'],
	['projectId', 'Project'],
	['clientId', 'Client'],
	['typeId', 'Type'],
	['subTypeId', 'Sub type'],
	['subTypeValueId', 'Value'],
];

const LOCATION_LABELS = [
	['precinctId', 'Precinct'],
	['blockId', 'Block'],
	['level', 'Level'],
	['unit', 'Unit'],
] as const;

/**
 * Every issue is tagged with the form field(s) it applies to, so the review
 * dialog can show it under that field. Messages are kept short enough to
 * read at a glance under a single input. `getWorkItemReviewIssues` (plain
 * strings, for summary lists) is derived from this single source of truth.
 */
export const getWorkItemFieldIssues = (
	item: ReviewableWorkItem,
	options: WorkItemReviewOptions = {}
): WorkItemReviewIssue[] => {
	const issues: WorkItemReviewIssue[] = FIELD_LABELS.filter(([key]) => item[key] === null).map(([key, label]) => ({
		fields: [key as WorkItemIssueField],
		message: `${label} is missing`,
	}));

	if (item.quantity === null || !Number.isFinite(item.quantity) || item.quantity <= 0) {
		issues.push({ fields: ['quantity'], message: 'Quantity must be more than 0' });
	}
	if (!item.quantityUnit.trim()) issues.push({ fields: ['quantityUnit'], message: 'Quantity unit is missing' });
	if (item.quantityUnit.length > 32) {
		issues.push({ fields: ['quantityUnit'], message: 'Quantity unit is too long (max 32)' });
	}
	if (!item.performedAt || Number.isNaN(new Date(item.performedAt).getTime())) {
		issues.push({ fields: ['performedAt'], message: 'Date is missing' });
	}

	if (options.requireStructuredLocation ?? true) {
		for (const [key, label] of LOCATION_LABELS) {
			if (item[key] === null) issues.push({ fields: [key], message: `${label} is missing` });
		}
	}
	if (item.reportedLocationText.length > 1000) {
		issues.push({
			fields: ['reportedLocationText'],
			message: 'Reported location is too long (max 1000)',
		});
	}

	// A level/unit read from the photo that doesn't fit the block usually means
	// the worker sent the wrong block, so the message is shown on both.
	const levelFrom = item.blockLevelFrom ?? 1;
	if (
		item.level !== null &&
		(item.level < levelFrom || (item.blockLevelTo !== null && item.level > item.blockLevelTo))
	) {
		issues.push({
			fields: ['level', 'blockId'],
			message: `Level ${item.level} is not in this block (levels ${levelFrom}–${item.blockLevelTo ?? '?'})`,
		});
	}
	if (
		item.unit !== null &&
		item.blockUnitFrom !== null &&
		item.blockUnitTo !== null &&
		(item.unit < item.blockUnitFrom || item.unit > item.blockUnitTo)
	) {
		issues.push({
			fields: ['unit', 'blockId'],
			message: `Unit ${item.unit} is not in this block (units ${item.blockUnitFrom}–${item.blockUnitTo})`,
		});
	}
	if (item.unit !== null && item.blockUnitNumberPattern === 'odd' && item.unit % 2 === 0) {
		issues.push({ fields: ['unit', 'blockId'], message: 'This block has odd unit numbers only' });
	}
	if (item.unit !== null && item.blockUnitNumberPattern === 'even' && item.unit % 2 !== 0) {
		issues.push({ fields: ['unit', 'blockId'], message: 'This block has even unit numbers only' });
	}

	if (item.workCategory !== 'normal' && item.isClientBillable) {
		issues.push({
			fields: ['workCategory'],
			message: 'This category cannot be client billable',
		});
	}
	if (!item.isWorkerCreditable) {
		issues.push({ fields: [], message: 'Work must be credited to a worker' });
	}
	if (item.userId !== null && item.helperUserIds.includes(item.userId)) {
		issues.push({ fields: ['userId'], message: 'Worker cannot also be a helper' });
	}

	return issues;
};

export const getWorkItemReviewIssues = (item: ReviewableWorkItem, options: WorkItemReviewOptions = {}): string[] =>
	getWorkItemFieldIssues(item, options).map((issue) => issue.message);

/** Which form controls should show a red/error border for this item. */
export const getWorkItemInvalidFields = (
	item: ReviewableWorkItem,
	options: WorkItemReviewOptions = {}
): Set<WorkItemIssueField> => new Set(getWorkItemFieldIssues(item, options).flatMap((issue) => issue.fields));

export const isWorkItemReady = (item: ReviewableWorkItem, options: WorkItemReviewOptions = {}): boolean =>
	getWorkItemReviewIssues(item, options).length === 0;
